import { apiClient } from "./client.api";
import { ProgramResponse, ProgramSkeletonResponse, ProgramSummaryResponse } from "../../types/program.response";
import { ProgramFulfillmentResponse } from "../../types/program-fulfillment.response";
import { cacheGet } from "../../utils/cacheGet";
import { stampMapKeys, toProgramSummary } from "../../utils/programData";

export const programsApi = {
    getProgram: async (id: string): Promise<ProgramResponse> => {
        const response = await apiClient.get(`/programs/${id}`);
        return response.data.data;
    },
    /**
     * 轻量 skeleton 档(契约 §4):courses 值为 CourseSummary,无重展示数据。
     * 端点尚未部署时(4xx)自动回退全量接口并归一,前端行为不变 —— 部署顺序安全。
     */
    getProgramSummary: async (id: string): Promise<ProgramSummaryResponse> => {
        try {
            const response = await apiClient.get(`/programs/${id}`, { params: { view: "summary" } });
            const data = response.data.data;
            // 后端尚未识别 view 参数、返回了全量形状(带 enrollGroups)时,走归一路径
            const sample = Object.values(data.courses ?? {})[0] as object | undefined;
            if (sample && "enrollGroups" in sample) {
                return toProgramSummary(data);
            }
            return { ...data, courses: stampMapKeys(data.courses) };
        } catch (err: any) {
            const status = err?.response?.status;
            if (status && status >= 400 && status < 500 && status !== 401) {
                const response = await apiClient.get(`/programs/${id}`);
                return toProgramSummary(response.data.data);
            }
            throw err;
        }
    },
    /**
     * skeleton 档(渐进加载契约 §4.1):完整 requirement 树 + 节点 summaries,
     * 零课程数据。首屏只阻塞在它上,课程摘要随后走 requirements= 切片补齐。
     */
    getProgramSkeleton: async (id: string): Promise<ProgramSkeletonResponse> => {
        const response = await apiClient.get(`/programs/${id}`, { params: { view: "skeleton" } });
        return response.data.data;
    },
    /**
     * requirement 级切片(渐进加载契约 §4.2):响应仍是 ProgramSummaryResponse,
     * requirements 数组过滤为指定项,courses map 切为其 requiredCourseIds 并集。
     * 响应是 (programId, requirementIds, structureVersion) 的纯函数,合并幂等。
     */
    getProgramRequirements: async (id: string, requirementIds: string[]): Promise<ProgramSummaryResponse> => {
        const response = await apiClient.get(`/programs/${id}`, {
            params: { view: "summary", requirements: requirementIds.join(",") },
        });
        const data = response.data.data;
        return { ...data, courses: stampMapKeys(data.courses) };
    },
    getProgramFulfillment: async (id: string): Promise<ProgramFulfillmentResponse> => {
        const response = await apiClient.get(`/programs/${id}/fulfillment`);
        return response.data.data;
    },
    /**
     * Broad(内涵)requirement 的推荐课(契约 §5):目录 ∩ 谓词 − 用户已有。
     * 只返回 courseIds(可直接作 /courses/batch 的 keys),与 structureVersion 无关。
     * 仅对树内有内涵节点的 requirement 有意义,其余后端 404。
     */
    getRequirementSuggestions: async (
        programId: string,
        requirementId: string,
        limit = 10,
    ): Promise<{ requirementId: string; nodeId: string; courseIds: string[] }> => {
        const response = await apiClient.get(
            `/programs/${programId}/requirements/${requirementId}/suggestions`,
            { params: { limit } },
        );
        return response.data.data;
    },
    /**
     * pool 推荐(轻量 fulfillment 契约 §2):match 池 = 目录 ∩ 谓词,
     * 外延池 = 成员差集 + 有用性过滤(封顶饱和/学分下限的课不推荐)。
     * 只回 courseIds,展示数据走 POST /courses/batch 水化。
     * courseRequirements:课号 → 挂靠 requirement ids(any_of 来源为空数组、match 池恒空)。
     * limit 默认 10 上限 50,匿名可用。详见《pool-推荐与成员可见性方案》§4。
     */
    getPoolSuggestions: async (
        programId: string,
        poolKey: string,
        limit = 10,
    ): Promise<{ poolKey: string; courseIds: string[]; courseRequirements: Record<string, string[]> }> => {
        const response = await apiClient.get(
            `/programs/${programId}/pools/${poolKey}/suggestions`,
            { params: { limit } },
        );
        return response.data.data;
    }
}